import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { lockBodyScroll } from "@scrile/tools/dist/lib/Scrollhelpers";
import ReactDOM from "react-dom";
import BaseButton from "components/BaseButton";
import BaseIcon from "components/BaseIcon";
import "./style.scss";

interface Props {
  show: boolean;
  modal?: boolean;
  children: React.ReactNode;
  className?: string;
  onClose: () => void;
  mobileFullScreen?: boolean;
}

type FsDocument = Document & {
  webkitFullscreenElement?: Element | null;
};

// The browser's "top layer" paints the native-fullscreen element and ITS
// DESCENDANTS above everything else, ignoring z-index. A portal mounted into
// document.body therefore renders BEHIND a fullscreen video and is unusable
// (this is the leave-call confirmation that's invisible in fullscreen).
//
// We resolve the portal host dynamically: when a native-fullscreen element
// exists AND can contain children, host the singleton #portal inside it so the
// modal joins the top layer; otherwise fall back to document.body (unchanged
// behaviour, including the CSS-overlay fullscreen fallback where
// document.fullscreenElement is null and z-index alone already works).
function getFullscreenHost(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const d = document as FsDocument;
  const el = (document.fullscreenElement || d.webkitFullscreenElement || null) as HTMLElement | null;
  if (!el) return null;
  // Replaced elements can't host child nodes — never reparent into them.
  if (el instanceof HTMLVideoElement || el instanceof HTMLImageElement || el instanceof HTMLCanvasElement) {
    return null;
  }
  return el;
}

function resolveHost(): HTMLElement {
  return getFullscreenHost() ?? document.body;
}

function ModalWindow({ show, modal, children, onClose, className, mobileFullScreen }: Props) {
  // Lazily resolve the singleton #portal node (shared across all ModalWindow
  // instances). We attach a freshly-created node to document.body immediately
  // so a second ModalWindow mounting in the same commit can discover it via
  // getElementById (otherwise we'd create duplicate #portal nodes). The layout
  // effect below then re-parents it to the fullscreen host when needed.
  const portalRef = useRef<HTMLElement | null>(null);
  if (portalRef.current === null) {
    const existing = document.getElementById("portal");
    if (existing) {
      portalRef.current = existing;
    } else {
      const el = document.createElement("div");
      el.id = "portal";
      document.body.appendChild(el);
      portalRef.current = el;
    }
  }

  const [host, setHost] = useState<HTMLElement>(() => resolveHost());

  const syncHost = useCallback(() => {
    setHost(resolveHost());
  }, []);

  // Follow the user into / out of native fullscreen. A modal already open moves
  // with them; a modal opened while already fullscreen starts in the right host
  // via the useState initialiser above.
  useEffect(() => {
    document.addEventListener("fullscreenchange", syncHost);
    document.addEventListener("webkitfullscreenchange", syncHost);
    return () => {
      document.removeEventListener("fullscreenchange", syncHost);
      document.removeEventListener("webkitfullscreenchange", syncHost);
    };
  }, [syncHost]);

  // Parent (and re-parent) the singleton into the resolved host before paint.
  // Placement is re-asserted on every mount (useState initialiser above) and on
  // every fullscreen transition (the listener updates `host`), and host is
  // computed identically from the shared fullscreen state across all instances,
  // so we never move the node to an invisible location. No cleanup re-home: if
  // a fullscreen subtree unmounts with #portal inside it, the node is simply
  // detached — getElementById won't find it, so the next modal creates a fresh
  // one (self-heal), and any modal that outlives the unmount is moved back to
  // body by the fullscreenchange the browser fires when the element is removed.
  // (An unconditional re-home here would instead break a second modal that is
  // still open in fullscreen when the first one closes.)
  useLayoutEffect(() => {
    const node = portalRef.current;
    if (node && node.parentElement !== host) {
      host.appendChild(node);
    }
  }, [host]);

  const cls = ["modal-window"];
  if (className) cls.push(className);
  if (mobileFullScreen) cls.push("__mobile-full-screen");

  let content: React.ReactNode = null;
  if (show) {
    content = (
      <div className={cls.join(" ")} onClick={(e) => e.stopPropagation()}>
        <div
          className="modal-window__backdrop"
          onClick={() => {
            if (modal) return;
            onClose();
          }}
        />
        <div className="modal-window__content">
          {!modal && (
            <BaseButton className="modal-window__close" opacity={true} onClick={onClose} icon={true}>
              <BaseIcon>close</BaseIcon>
            </BaseButton>
          )}
          {children}
        </div>
      </div>
    );
  }

  // Body-scroll lock is global to document.body and independent of the portal
  // host, so key it on `show` only.
  useEffect(() => {
    if (!show) return;
    lockBodyScroll();
    return () => {
      lockBodyScroll(false);
    };
  }, [show]);

  return ReactDOM.createPortal(content, portalRef.current);
}

export default ModalWindow;
